<!-- SessionExpirationWarning.vue -->
<template>
  <div
    v-if="visible"
    class="session-expiration-modal"
  >
    <div class="modal-content">
      <h3>{{ $t("sessionExpired.title") }}</h3>
      <p>{{ $t("sessionExpired.message", { formattedTimeRemaining }) }}.</p>
      <p>{{ $t("sessionExpired.question") }}</p>
      <div class="modal-actions">
        <button
          class="btn-primary"
          @click="refreshSession"
        >
          {{ $t("sessionExpired.stay") }}
        </button>
        <button
          class="btn-secondary"
          @click="logout"
        >
          {{ $t("sessionExpired.logout") }}
        </button>
      </div>
      <div class="progress-bar">
        <div
          class="progress"
          :style="{ width: progressWidth + '%' }"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, computed, onUnmounted, Ref } from "vue";
import sessionService from "@/services/SessionService";
import services from "@/composable/services";
import "@/style/views/SessionExpirationWarning.scss";

export default {
  setup() {
    const visible = ref(false);
    const remainingSeconds = ref(0);
    const countdownInterval: Ref<NodeJS.Timeout | undefined> = ref(undefined);

    const formattedTimeRemaining = computed(() => {
      const minutes = Math.floor(remainingSeconds.value / 60);
      const seconds = remainingSeconds.value % 60;
      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    });

    const progressWidth = computed(() => {
      // Calculer le pourcentage de temps restant par rapport au seuil d'avertissement
      const warningThreshold = sessionService.warningThreshold / 1000;
      return (remainingSeconds.value / warningThreshold) * 100;
    });

    onUnmounted(() => {
      // Arrêter le compte à rebours
      if (countdownInterval.value) {
        clearInterval(countdownInterval.value);
      }
    });
    function showWarning(remainingSecondsValue: number) {
      // Réinitialiser complètement l'état précédent
      if (countdownInterval.value) {
        clearInterval(countdownInterval.value);
        countdownInterval.value = undefined;
      }

      visible.value = true;
      remainingSeconds.value = remainingSecondsValue;

      // Redémarrer le compteur avec les nouvelles valeurs
      countdownInterval.value = setInterval(() => {
        remainingSeconds.value = Math.max(0, remainingSeconds.value - 1);

        if (remainingSeconds.value <= 0) {
          clearInterval(countdownInterval.value);
          logout();
        }
      }, 1000);
    }

    function hideWarning() {
      visible.value = false;
      if (countdownInterval.value) {
        clearInterval(countdownInterval.value);
      }
    }

    function refreshSession() {
      // Bug fix : si on ne stoppe pas le compte à rebours local AVANT
      // d'attendre la réponse de POST /refresh , il peut atteindre 0
      // pendant la requête réseau et appeler logout() ci-dessous ,
      // déconnectant l'utilisateur alors qu'il vient de cliquer "Rester
      // connecté". On masque aussi la modal tout de suite pour un
      // feedback visuel immédiat ; SessionService la rouvrira si besoin.
      if (countdownInterval.value) {
        clearInterval(countdownInterval.value);
        countdownInterval.value = undefined;
      }
      visible.value = false;
      sessionService.refreshSession();
    }

    function logout() {
      services.loginService.logout().finally(() => {
        visible.value = false; // Fermer la modale explicitement
        remainingSeconds.value = 0;
      });
    }

    return {
      visible,
      remainingSeconds,
      countdownInterval,
      formattedTimeRemaining,
      progressWidth,
      showWarning,
      hideWarning,
      refreshSession,
      logout,
    };
  },
};
</script>
